import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { AdditionalModule, INTEGRATION_FEATURES, SubscriptionData, SubscriptionPackage } from '../BillingTypes';
import { formatPriceOnUserLocale } from '../functions/billingUtils';
import ModuleRowCard from '../ModuleRowCard';
import IntegrationModuleCard from './IntegrationModuleCard';
import '../../../../style/scss/billing.scss';

interface ModulesSelectionProps {
    packages: SubscriptionPackage[];
    additionalModules: AdditionalModule[];
    currentSubscription: SubscriptionData;
    updatedSubscription: SubscriptionData;
    setUpdatedSubscription: React.Dispatch<React.SetStateAction<SubscriptionData>>;
};

const ModulesSelection: React.FC<ModulesSelectionProps> = ({
    packages, additionalModules, currentSubscription, updatedSubscription, setUpdatedSubscription
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Find the selected package
    const selectedPackageObject = packages.find(pkg => pkg.key === updatedSubscription.package);

    // Get the active api tier lines when an existing subscription is changed, so they are shown as active
    const apiTierLines = currentSubscription.is_trial ? [] : (currentSubscription.subscription_lines || [])
        .filter(line => line.type === 'additional_module' && line.is_active && (line.product_name || '').startsWith('rest_api_'));

    // Summarize the active api tiers with their amount of api keys
    const apiTierSummary = Object.entries(
        apiTierLines.reduce((tiers: Record<string, number>, line) => {
            const name = line.product_name as string;
            tiers[name] = (tiers[name] || 0) + (line.quantity || 1);
            return tiers;
        }, {})
    ).map(([name, count]) => `${count}× ${t(`billing.feature.${name}`)}`).join(', ');

    // Calculate the total monthly price of the active api tier lines
    const apiTierTotalPrice = apiTierLines.reduce((total, line) => total + (parseFloat(line.price) * (line.quantity || 1)), 0);

    // Get the orderable module options of the selected package without the integrations, sorted by their ordering
    const moduleOptions = additionalModules
        .filter(module => module.is_active && (selectedPackageObject?.modules || []).includes(module.feature))
        .filter(module => !INTEGRATION_FEATURES.includes(module.feature))
        .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));

    // Get the integration module options which are shown as one consolidated card
    const integrationModules = additionalModules
        .filter(module => module.is_active && INTEGRATION_FEATURES.includes(module.feature));

    // Get the selected module names of the updated subscription
    const selectedModuleNames = (updatedSubscription.additional_modules || []).map(module => module.name);

    // Toggle the selection of a module
    const handleModuleToggle = (moduleKey: string) => {
        setUpdatedSubscription(prev => {

            // Get the current selected modules
            const currentModules = prev.additional_modules || [];

            // Remove the module if it is already selected
            if (currentModules.some(module => module.name === moduleKey)) {
                return { ...prev, additional_modules: currentModules.filter(module => module.name !== moduleKey) };
            }

            // Otherwise add the module to the selection
            return { ...prev, additional_modules: [...currentModules, { name: moduleKey }] };
        });
    };

    return (
        <div className='wizard-step'>
            <div className='container-filled header-title'>
                <h2>{t('billing.general.select_modules_header')}</h2>
                <p>{t('billing.general.select_modules_description')}</p>
            </div>
            <div className='step-content'>
                {moduleOptions.length > 0 || integrationModules.length > 0 || apiTierLines.length > 0 ? (
                    <div className='container-filled module-row-cards'>
                        {moduleOptions.map(module => (
                            <ModuleRowCard
                                key={module.feature}
                                module={module}
                                selected={selectedModuleNames.includes(module.feature)}
                                onClick={() => handleModuleToggle(module.feature)}
                            />
                        ))}
                        {integrationModules.length > 0 && (
                            <IntegrationModuleCard
                                integrationModules={integrationModules}
                                currentSubscription={currentSubscription}
                                updatedSubscription={updatedSubscription}
                                setUpdatedSubscription={setUpdatedSubscription}
                            />
                        )}
                        {apiTierLines.length > 0 && (
                            // Show the active api key tiers of the current subscription as active card
                            <div className='module-row-card api-keys-module-card selected'>
                                <div className='module-info'>
                                    <h5>{t('billing.module_price.api_keys_card_title')}</h5>
                                    <p>{apiTierSummary}</p>
                                    <p>{t('billing.module_price.api_keys_card_description')}</p>
                                </div>
                                <div className='module-price'>
                                    <div className='price-label'>
                                        <span className='currency-icon'>€ </span>
                                        {formatPriceOnUserLocale(apiTierTotalPrice, userLocale)}
                                        <span className='price-period'> {t('billing.module_price.per_month_suffix')}</span>
                                    </div>
                                    <div className='price-unit-label'>
                                        {t('api_key.general.per_api_key_label')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className='container-filled'>
                        <p>{t('billing.general.no_modules_available_label')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModulesSelection;
